import { FC } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useLocation } from 'react-router-dom';
import { ProtectedRouteConfig } from '../types';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

// Default loading component
const DefaultLoadingComponent = () => (
  <Box sx={{ display: 'flex' }}>
    <CircularProgress />
  </Box>
);

export interface ProtectedRouteProps extends ProtectedRouteConfig {
  component: React.ComponentType<any>;
  [key: string]: any;
}

export const createProtectedRoute = (config: ProtectedRouteConfig = {}) => {
  const {
    LoadingComponent = DefaultLoadingComponent,
    captureRedirectUrl = true,
  } = config;

  const ProtectedRoute: FC<ProtectedRouteProps> = ({ 
    component: Component, 
    LoadingComponent: CustomLoadingComponent,
    ...rest 
  }) => {
    const { authState, oktaAuth } = useOktaAuth();
    const location = useLocation();

    const LoadingComponentToUse = CustomLoadingComponent || LoadingComponent;

    if (!authState) {
      return <LoadingComponentToUse />;
    }

    if (!authState.isAuthenticated) {
      if (captureRedirectUrl) {
        // Pass the current location to Okta so it knows where to redirect after auth
        const currentPath = location.pathname + location.search;
        oktaAuth.signInWithRedirect({ originalUri: currentPath });
      } else {
        oktaAuth.signInWithRedirect();
      }
      return null;
    } else {
      return <Component {...rest} />;
    }
  };

  return ProtectedRoute;
};
